import { takeLatest, call, put, select } from 'redux-saga/effects'
import {
    USERS_REQUEST,
    usersSuccess,
    usersError,
    USER_REQUEST,
    userRequest,
    userSuccess,
    userError,
    ROLES_REQUEST,
    rolesRequest,
    rolesRequestSuccess,
    rolesRequestError,
    USER_UPDATE_REQUEST,
    userUpdateSuccess,
    userUpdateError,
    USER_CREATE_REQUEST,
    userCreateSuccess,
    userCreateError,
    USERS_DELETE_REQUEST,
    usersDeleteSuccess,
    usersDeleteError,
    usersCheckMembershipSuccess,
    usersCheckMembershipError,
    USERS_CHECK_MEMBERSHIP_REQUEST,
    usersAddToCustomerSuccess,
    usersAddToCustomerError,
    USERS_ADD_TO_CUSTOMER_REQUEST,
    usersRequest,
    customersRequestSuccess,
    customersRequestError,
    CUSTOMERS_REQUEST,
    roleRequestSuccess,
    roleRequestError,
    ROLE_REQUEST,
    roleCreateSuccess,
    roleCreateError,
    ROLE_CREATE,
    roleUpdateSuccess,
    roleUpdateError,
    ROLE_UPDATE,
    DELETE_CUSTOM_ROLES
} from './actions'
import { startLoading, stopLoading, enqueueSnackbar } from '../root/actions'
import axios from 'axios'
import { buildArrayQueryString } from '../../utils'
import { getSagasMsgs } from '../root/selectors'
import qs from 'qs'

export const watchUserManagementSaga = [
    takeLatest(USERS_REQUEST, requestUsers),
    takeLatest(USER_REQUEST, requestUser),
    takeLatest(ROLES_REQUEST, requestRoles),
    takeLatest(CUSTOMERS_REQUEST, getCustomers),
    takeLatest(USER_UPDATE_REQUEST, requestUserUpdate),
    takeLatest(USER_CREATE_REQUEST, requestUserCreate),
    takeLatest(USERS_DELETE_REQUEST, requestUsersDelete),
    takeLatest(USERS_CHECK_MEMBERSHIP_REQUEST, requestUsersMembershipCheck),
    takeLatest(USERS_ADD_TO_CUSTOMER_REQUEST, requestUsersAddToCustomer),
    takeLatest(ROLE_REQUEST, getRole),
    takeLatest(ROLE_CREATE, createRole),
    takeLatest(ROLE_UPDATE, updateRole),
    takeLatest(DELETE_CUSTOM_ROLES, requestDeleteCustomRoles)
]

function* requestUsers(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        const response = yield call(fetchUsers, action.filters)
        yield put(usersSuccess(response))
    } catch (ex) {
        yield put(usersError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.getUsersEx,
                options: {
                    variant: 'error'
                }
            })
        )
    }
}

async function fetchUsers(filters) {
    const response = await axios.get(`${process.env.API_URL}/users`, {
        params: {
            ...filters
        },
        paramsSerializer: params => {
            return qs.stringify({ ...params })
        }
    })
    return response.data
}

function* requestUser(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        yield put(startLoading())
        const response = yield call(fetchUser, action.userId)
        const rolesResponse = yield call(fetchRoles)
        yield put(rolesRequestSuccess(rolesResponse))
        yield put(userSuccess(response))
        yield put(stopLoading())
    } catch (ex) {
        yield put(userError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.getUserEx,
                options: {
                    variant: 'error'
                }
            })
        )
        yield put(stopLoading())
    }
}

async function fetchUser(userId) {
    const response = await axios.get(`${process.env.API_URL}/users/${userId}`)
    return response.data
}

function* getRole(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        yield put(startLoading())
        const response = yield call(getRoleRequest, action.roleId)
        yield put(roleRequestSuccess(response))
        yield put(stopLoading())
    } catch (ex) {
        yield put(roleRequestError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.getRoleEx,
                options: {
                    variant: 'error'
                }
            })
        )
        yield put(stopLoading())
    }
}

async function getRoleRequest(roleId) {
    const response = await axios.get(`${process.env.API_URL}/roles/${roleId}`)
    return response.data
}

function* createRole(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        yield put(startLoading())
        const response = yield call(createRoleRequest, action.body)
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.createRoleRes,
                options: {
                    variant: 'success'
                }
            })
        )
        yield put(roleCreateSuccess(response))
        yield put(stopLoading())
    } catch (ex) {
        yield put(roleCreateError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.createRoleEx,
                options: {
                    variant: 'error'
                }
            })
        )
        yield put(stopLoading())
    }
}

async function createRoleRequest(body) {
    const response = await axios.post(`${process.env.API_URL}/roles/`, body)
    return response.data
}

function* updateRole(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        yield put(startLoading())
        yield call(updateRoleRequest, action.roleId, action.body)
        const response = yield call(getRoleRequest, action.roleId)
        yield put(roleRequestSuccess(response))
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.updateRoleRes,
                options: {
                    variant: 'success'
                }
            })
        )
        yield put(roleUpdateSuccess())
        yield put(stopLoading())
    } catch (ex) {
        yield put(roleUpdateError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.updateRoleEx,
                options: {
                    variant: 'error'
                }
            })
        )
        yield put(stopLoading())
    }
}

async function updateRoleRequest(roleId, body) {
    await axios.patch(`${process.env.API_URL}/roles/${roleId}`, body)
}

function* requestRoles(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        if (!action.noLoadingState) yield put(startLoading())
        const response = yield call(fetchRoles)
        yield put(rolesRequestSuccess(response))
        yield put(stopLoading())
    } catch (ex) {
        yield put(rolesRequestError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.getRolesEx,
                options: {
                    variant: 'error'
                }
            })
        )
        yield put(stopLoading())
    }
}

async function fetchRoles() {
    const response = await axios.get(`${process.env.API_URL}/roles`)
    return response.data
}

function* getCustomers(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        yield put(startLoading())
        const response = yield call(getCustomersRequest)
        yield put(customersRequestSuccess(response))
        yield put(stopLoading())
    } catch (ex) {
        yield put(customersRequestError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.getCustomersEx,
                options: {
                    variant: 'error'
                }
            })
        )
        yield put(stopLoading())
    }
}

async function getCustomersRequest() {
    const response = await axios.get(`${process.env.API_URL}/customers`)
    return response.data
}

function* requestUserUpdate(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        yield put(startLoading())
        yield call(updateUser, action.userId, action.userBody)
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.updateUserRes,
                options: {
                    variant: 'success'
                }
            })
        )
        yield put(userUpdateSuccess())
        yield put(userRequest(action.userId))
        yield put(stopLoading())
    } catch (ex) {
        yield put(userUpdateError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.updateUserEx,
                options: {
                    variant: 'error'
                }
            })
        )
        yield put(stopLoading())
    }
}

async function updateUser(userId, userBody) {
    await axios.patch(`${process.env.API_URL}/users/${userId}`, userBody)
}

function* requestUserCreate(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        yield put(startLoading())
        const response = yield call(createUser, action.userBody)
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.createUserRes,
                options: {
                    variant: 'success'
                }
            })
        )
        yield put(userCreateSuccess(response))
        yield put(userRequest(response))
        yield put(stopLoading())
    } catch (ex) {
        yield put(userCreateError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.createUserEx,
                options: {
                    variant: 'error'
                }
            })
        )
        yield put(stopLoading())
    }
}

async function createUser(userBody) {
    const response = await axios.post(`${process.env.API_URL}/users/`, userBody)
    return response.data
}

function* requestUsersDelete(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        yield put(startLoading())
        yield call(deleteUsers, action.deleteBody)
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.deleteUserRes,
                options: {
                    variant: 'success'
                }
            })
        )
        yield put(usersDeleteSuccess())
        yield put(usersRequest())
        yield put(stopLoading())
    } catch (ex) {
        yield put(usersDeleteError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.deleteUserEx,
                options: {
                    variant: 'error'
                }
            })
        )
        yield put(stopLoading())
    }
}

async function deleteUsers(deleteBody) {
    await axios.delete(`${process.env.API_URL}/users/`, {
        data: deleteBody
    })
}

function* requestUsersMembershipCheck(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        yield put(startLoading())
        const response = yield call(checkMembershipUsers, action.emails)
        yield put(usersCheckMembershipSuccess(response))
        yield put(stopLoading())
    } catch (ex) {
        yield put(usersCheckMembershipError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.membershipEx,
                options: {
                    variant: 'error'
                }
            })
        )
        yield put(stopLoading())
    }
}

async function checkMembershipUsers(emails) {
    const arraqQueryStr = buildArrayQueryString(emails, 'emails')
    const response = await axios.get(
        `${process.env.API_URL}/users/check-membership${arraqQueryStr}`
    )
    return response.data
}

function* requestUsersAddToCustomer(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        yield put(startLoading())
        yield call(addUsersToCustomer, action.body)
        yield put(usersAddToCustomerSuccess())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.addToOrgRes,
                options: {
                    variant: 'success'
                }
            })
        )
        yield put(usersRequest())
        yield put(stopLoading())
    } catch (ex) {
        yield put(usersAddToCustomerError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.addToOrgEx,
                options: {
                    variant: 'error'
                }
            })
        )
        yield put(stopLoading())
    }
}

async function addUsersToCustomer(body) {
    await axios.post(`${process.env.API_URL}/users/add-to-customer`, body)
}

function* requestDeleteCustomRoles(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        yield put(startLoading())
        const users = yield call(deleteCustomRoles, action.body.roleId)

        if (!users) {
            yield put(
                enqueueSnackbar({
                    message: sagasMsgs.deleteRoleRes,
                    options: {
                        variant: 'success'
                    }
                })
            )
            yield put(rolesRequest())
        } else {
            yield call(action.body.setUsersAssignedRoleInfoModalOpen, true)
        }
        yield put(stopLoading())
    } catch (ex) {
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.deleteRoleEx,
                options: {
                    variant: 'error'
                }
            })
        )
        yield put(stopLoading())
    }
}

async function deleteCustomRoles(roleId) {
    const response = await axios.delete(
        `${process.env.API_URL}/roles/${roleId}`
    )
    return response.data.users
}
